import React, { useState } from 'react';
import { graphBuilderApi } from '../../services/api';
import './Explainability.css';

/**
 * WhatIfPanel
 *
 * Counterfactual scenario builder. Lets the auditor pick an evidence
 * factor or rule, choose an action (remove / weaken / disable), and
 * see how the decision would change. Calls POST /api/explain/what-if.
 *
 * Props:
 *  - queryRunId:        required to call /what-if
 *  - domain:            vocabulary domain
 *  - evidenceFactors:   list of {name, used_in_answer, ...} for the picker
 *  - decisionRules:     list of {rule_name, ...} for the picker
 */
const WhatIfPanel = ({ queryRunId, domain = 'general', evidenceFactors = [], decisionRules = [] }) => {
  const [scenarios, setScenarios] = useState([
    { id: 1, modification_type: 'remove_evidence', target_label: '', description: '' },
  ]);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const usedFactors = evidenceFactors.filter((f) => f.used_in_answer);
  const factorOptions = usedFactors.length ? usedFactors : evidenceFactors;

  const updateScenario = (idx, patch) => {
    setScenarios((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const addScenario = () => {
    setScenarios((prev) => [
      ...prev,
      {
        id: Date.now(),
        modification_type: 'remove_evidence',
        target_label: '',
        description: '',
      },
    ]);
  };

  const removeScenario = (idx) => {
    setScenarios((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRun = async () => {
    if (!queryRunId) {
      setError('Cannot run what-if: this evaluation is missing the query_run_id required by the analysis engine.');
      return;
    }
    const validScenarios = scenarios
      .filter((s) => s.target_label || s.description)
      .map((s) => ({
        description: s.description || `${s.modification_type.replace('_', ' ')} ${s.target_label}`,
        modification_type: s.modification_type,
        target_label: s.target_label,
        new_value: s.modification_type === 'change_strength' ? Number(s.new_value || 0) : null,
      }));

    if (!validScenarios.length) {
      setError('Add at least one scenario with a target before running.');
      return;
    }

    setRunning(true);
    setError(null);
    try {
      const data = await graphBuilderApi.runWhatIf({
        queryRunId,
        scenarios: validScenarios,
        domain,
      });
      setResults(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'What-if analysis failed.');
    } finally {
      setRunning(false);
    }
  };

  if (!queryRunId) {
    return (
      <div className="explain-whatif-panel explain-whatif-panel--muted">
        <h4 className="explain-whatif-title">What-If Analysis</h4>
        <p className="explain-whatif-note">
          What-if scenarios are only available for evaluations run via
          the streaming Test Graph path.
        </p>
      </div>
    );
  }

  return (
    <div className="explain-whatif-panel">
      <h4 className="explain-whatif-title">What-If Analysis</h4>
      <p className="explain-whatif-note">
        Test how the decision would change if a specific piece of evidence were removed,
        weakened, or a rule disabled.
      </p>

      <div className="explain-whatif-scenarios">
        {scenarios.map((s, idx) => (
          <div key={s.id} className="explain-whatif-scenario">
            <div className="explain-whatif-scenario-row">
              <select
                className="explain-whatif-select"
                value={s.modification_type}
                onChange={(e) => updateScenario(idx, { modification_type: e.target.value })}
              >
                <option value="remove_evidence">Remove evidence</option>
                <option value="change_strength">Weaken evidence</option>
                <option value="disable_rule">Disable rule</option>
              </select>

              <select
                className="explain-whatif-select"
                value={s.target_label}
                onChange={(e) => updateScenario(idx, { target_label: e.target.value })}
              >
                <option value="">Select target…</option>
                {s.modification_type === 'disable_rule'
                  ? decisionRules.map((r, i) => (
                      <option key={i} value={r.rule_name}>{r.rule_name}</option>
                    ))
                  : factorOptions.map((f, i) => (
                      <option key={i} value={f.name}>
                        {f.name}{f.cite_num ? ` [${f.cite_num}]` : ''}
                      </option>
                    ))}
              </select>

              {s.modification_type === 'change_strength' && (
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  placeholder="New strength (0-1)"
                  className="explain-whatif-input"
                  value={s.new_value ?? ''}
                  onChange={(e) => updateScenario(idx, { new_value: e.target.value })}
                />
              )}

              {scenarios.length > 1 && (
                <button
                  type="button"
                  className="explain-whatif-remove-btn"
                  onClick={() => removeScenario(idx)}
                  aria-label="Remove scenario"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        ))}

        <button type="button" className="explain-whatif-add-btn" onClick={addScenario}>
          + Add scenario
        </button>
      </div>

      <div className="explain-whatif-actions">
        <button
          type="button"
          className="explain-whatif-run-btn"
          onClick={handleRun}
          disabled={running}
        >
          {running ? 'Running…' : 'Run analysis'}
        </button>
      </div>

      {error && <div className="explain-whatif-error">{error}</div>}

      {results?.scenarios && (
        <div className="explain-whatif-results">
          <h5 className="explain-whatif-results-title">Results</h5>
          <ul className="explain-whatif-results-list">
            {results.scenarios.map((r, i) => (
              <li key={i} className="explain-whatif-result-item">
                <div className="explain-whatif-result-header">
                  <strong>{r.description || `Scenario ${i + 1}`}</strong>
                  {r.changes_decision !== undefined && (
                    <span className={`explain-whatif-flip-badge explain-whatif-flip-badge--${r.changes_decision ? 'flipped' : 'stable'}`}>
                      {r.changes_decision ? 'Decision changes' : 'Decision unchanged'}
                    </span>
                  )}
                </div>
                {r.new_decision && (
                  <p className="explain-whatif-result-note">
                    New outcome: <strong>{r.new_decision}</strong>
                  </p>
                )}
                {r.explanation && (
                  <p className="explain-whatif-result-note">{r.explanation}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default WhatIfPanel;
